import { Modal, Descriptions, Tag, Button, Space, Timeline, Tabs, Input, Spin, App } from 'antd';
import {
  CheckCircleOutlined,
  CloseCircleOutlined,
  ClockCircleOutlined,
  SyncOutlined,
  ExclamationCircleOutlined,
  StopOutlined,
} from '@ant-design/icons';
import {
  TransactionQueue,
  TransactionQueueStatus,
  TransactionStateLog,
  TransactionQueueWithUser,
} from '@mygate/common';
import { useState } from 'react';
import { transactionQueueService } from '@/services/transactionQueue';
import { formatDateTime } from '@/utils/date';

interface TransactionDetailModalProps {
  visible: boolean;
  transaction: TransactionQueue | null;
  onClose: () => void;
  onActionComplete: () => void;
  loading?: boolean;
}

export function TransactionDetailModal({
  visible,
  transaction,
  onClose,
  onActionComplete,
  loading = false,
}: TransactionDetailModalProps) {
  const { modal, message } = App.useApp();
  const [actionLoading, setActionLoading] = useState(false);
  const [rejectReason, setRejectReason] = useState('');
  const [showRejectInput, setShowRejectInput] = useState(false);

  if (!transaction) {
    return null;
  }

  const getStatusColor = (status: TransactionQueueStatus) => {
    switch (status) {
      case TransactionQueueStatus.GENERATED:
        return 'default';
      case TransactionQueueStatus.PENDING_APPROVAL:
        return 'warning';
      case TransactionQueueStatus.APPROVED:
        return 'processing';
      case TransactionQueueStatus.PROCESSING:
        return 'processing';
      case TransactionQueueStatus.COMPLETED:
        return 'success';
      case TransactionQueueStatus.FAILED:
        return 'error';
      case TransactionQueueStatus.CANCELED:
        return 'default';
      default:
        return 'default';
    }
  };

  const getStatusIcon = (status: TransactionQueueStatus) => {
    switch (status) {
      case TransactionQueueStatus.PENDING_APPROVAL:
        return <ClockCircleOutlined />;
      case TransactionQueueStatus.APPROVED:
        return <CheckCircleOutlined />;
      case TransactionQueueStatus.PROCESSING:
        return <SyncOutlined spin />;
      case TransactionQueueStatus.COMPLETED:
        return <CheckCircleOutlined />;
      case TransactionQueueStatus.FAILED:
        return <CloseCircleOutlined />;
      case TransactionQueueStatus.CANCELED:
        return <StopOutlined />;
      default:
        return <ExclamationCircleOutlined />;
    }
  };

  const handleApprove = async () => {
    setActionLoading(true);
    try {
      await transactionQueueService.approveTransaction(transaction.id);
      message.success('Transaction approved successfully');
      onActionComplete();
      onClose();
    } catch (error: any) {
      message.error(error.message || 'Failed to approve transaction');
    } finally {
      setActionLoading(false);
    }
  };

  const handleReject = async () => {
    if (!rejectReason.trim()) {
      message.error('Please provide a rejection reason');
      return;
    }

    setActionLoading(true);
    try {
      await transactionQueueService.rejectTransaction(transaction.id, rejectReason);
      message.success('Transaction rejected');
      onActionComplete();
      onClose();
    } catch (error: any) {
      message.error(error.message || 'Failed to reject transaction');
    } finally {
      setActionLoading(false);
      setShowRejectInput(false);
      setRejectReason('');
    }
  };

  const handleRetry = async () => {
    setActionLoading(true);
    try {
      await transactionQueueService.retryTransaction(transaction.id);
      message.success('Transaction queued for retry');
      onActionComplete();
      onClose();
    } catch (error: any) {
      message.error(error.message || 'Failed to retry transaction');
    } finally {
      setActionLoading(false);
    }
  };

  const handleCancel = async () => {
    modal.confirm({
      title: 'Cancel Transaction',
      content: 'Are you sure you want to cancel this transaction?',
      onOk: async () => {
        setActionLoading(true);
        try {
          await transactionQueueService.cancelTransaction(transaction.id, 'Manually cancelled by staff');
          message.success('Transaction cancelled');
          onActionComplete();
          onClose();
        } catch (error: any) {
          message.error(error.message || 'Failed to cancel transaction');
        } finally {
          setActionLoading(false);
        }
      },
    });
  };

  const formatAmount = (amount: string) => {
    return new Intl.NumberFormat('de-DE', {
      style: 'currency',
      currency: transaction.currency || 'EUR',
    }).format(parseFloat(amount));
  };

  return (
    <Modal
      title="Transaction Details"
      open={visible}
      onCancel={onClose}
      width={800}
      footer={[
        <Button key="close" onClick={onClose}>
          Close
        </Button>,
        transaction.status === TransactionQueueStatus.PENDING_APPROVAL && (
          <>
            {!showRejectInput ? (
              <>
                <Button key="reject" danger onClick={() => setShowRejectInput(true)} loading={actionLoading}>
                  Reject
                </Button>
                <Button key="approve" type="primary" onClick={handleApprove} loading={actionLoading}>
                  Approve
                </Button>
              </>
            ) : (
              <Space wrap key="reject-input">
                <Input
                  placeholder="Rejection reason"
                  value={rejectReason}
                  onChange={e => setRejectReason(e.target.value)}
                  style={{ width: 300 }}
                />
                <Button
                  onClick={() => {
                    setShowRejectInput(false);
                    setRejectReason('');
                  }}
                >
                  Cancel
                </Button>
                <Button danger onClick={handleReject} loading={actionLoading}>
                  Confirm Reject
                </Button>
              </Space>
            )}
          </>
        ),
        transaction.status === TransactionQueueStatus.FAILED && (
          <Button key="retry" type="primary" onClick={handleRetry} loading={actionLoading}>
            Retry
          </Button>
        ),
        (transaction.status === TransactionQueueStatus.APPROVED ||
          transaction.status === TransactionQueueStatus.GENERATED) && (
          <Button key="cancel" danger onClick={handleCancel} loading={actionLoading}>
            Cancel
          </Button>
        ),
      ]}
    >
      {loading ? (
        <div style={{ textAlign: 'center', padding: '40px' }}>
          <Spin size="large" />
        </div>
      ) : (
        <Tabs defaultActiveKey="details">
          <Tabs.TabPane tab="Details" key="details">
            <Descriptions bordered column={{ xs: 1, sm: 2, md: 2, lg: 2, xl: 2, xxl: 2 }}>
              <Descriptions.Item label="Transaction ID" span="filled">
                {transaction.id}
              </Descriptions.Item>
              {(transaction as TransactionQueueWithUser).user && (
                <>
                  <Descriptions.Item label="User Name" span={1}>
                    {`${(transaction as TransactionQueueWithUser).user.firstName || ''} ${(transaction as TransactionQueueWithUser).user.lastName || ''}`.trim() ||
                      '-'}
                  </Descriptions.Item>
                  <Descriptions.Item label="Phone Number" span={1}>
                    {(transaction as TransactionQueueWithUser).user.phoneNumber || '-'}
                  </Descriptions.Item>
                  {(transaction as TransactionQueueWithUser).user.email && (
                    <Descriptions.Item label="Email" span="filled">
                      {(transaction as TransactionQueueWithUser).user.email}
                    </Descriptions.Item>
                  )}
                </>
              )}
              <Descriptions.Item label="Status" span={1}>
                <Tag icon={getStatusIcon(transaction.status)} color={getStatusColor(transaction.status)}>
                  {transaction.status}
                </Tag>
              </Descriptions.Item>
              <Descriptions.Item label="Amount" span={1}>
                <strong>{formatAmount(transaction.amount)}</strong>
              </Descriptions.Item>
              <Descriptions.Item label="Recipient" span="filled">
                {transaction.recipient}
              </Descriptions.Item>
              <Descriptions.Item label="IBAN" span={1}>
                {transaction.iban}
              </Descriptions.Item>
              <Descriptions.Item label="BIC" span={1}>
                {transaction.bic}
              </Descriptions.Item>
              <Descriptions.Item label="Bank Name" span="filled">
                {transaction.bankname}
              </Descriptions.Item>
              <Descriptions.Item label="Purpose" span="filled">
                {transaction.purpose}
              </Descriptions.Item>
              <Descriptions.Item label="Due Date" span={1}>
                {formatDateTime(transaction.dueDate)}
              </Descriptions.Item>
              <Descriptions.Item label="Generated At" span={1}>
                {formatDateTime(transaction.generatedAt)}
              </Descriptions.Item>
              {transaction.approvedBy && (
                <>
                  <Descriptions.Item label="Approved By" span={1}>
                    {transaction.approvedBy}
                  </Descriptions.Item>
                  <Descriptions.Item label="Approved At" span={1}>
                    {transaction.approvedAt && formatDateTime(transaction.approvedAt)}
                  </Descriptions.Item>
                </>
              )}
              {transaction.processedAt && (
                <Descriptions.Item label="Processed At" span={1}>
                  {formatDateTime(transaction.processedAt)}
                </Descriptions.Item>
              )}
              {transaction.completedAt && (
                <Descriptions.Item label="Completed At" span={1}>
                  {formatDateTime(transaction.completedAt)}
                </Descriptions.Item>
              )}
              {transaction.failureReason && (
                <Descriptions.Item label="Failure Reason" span="filled">
                  <span style={{ color: '#ff4d4f' }}>{transaction.failureReason}</span>
                </Descriptions.Item>
              )}
              {transaction.retryCount > 0 && (
                <Descriptions.Item label="Retry Count" span={1}>
                  {transaction.retryCount}
                </Descriptions.Item>
              )}
              {transaction.paycenterJobId && (
                <Descriptions.Item label="PayCenter Job ID" span="filled">
                  <code>{transaction.paycenterJobId}</code>
                </Descriptions.Item>
              )}
            </Descriptions>
          </Tabs.TabPane>

          <Tabs.TabPane tab="History" key="history">
            <Timeline mode="left">
              {transaction.stateTransitions.map((transition: TransactionStateLog, index: number) => (
                <Timeline.Item
                  key={index}
                  color={getStatusColor(transition.toStatus)}
                  label={formatDateTime(transition.changedAt)}
                >
                  <p>
                    <strong>
                      {transition.fromStatus || 'Initial'} → {transition.toStatus}
                    </strong>
                  </p>
                  {transition.reason && <p>Reason: {transition.reason}</p>}
                  {transition.changedBy && <p>By: {transition.changedBy}</p>}
                </Timeline.Item>
              ))}
            </Timeline>
          </Tabs.TabPane>

          {(transaction.paycenterRequest || transaction.paycenterResponse) && (
            <Tabs.TabPane tab="PayCenter Data" key="paycenter">
              {transaction.paycenterRequest && (
                <>
                  <h4>Request</h4>
                  <pre style={{ background: '#f5f5f5', padding: '10px', borderRadius: '4px', overflow: 'auto' }}>
                    {JSON.stringify(transaction.paycenterRequest, null, 2)}
                  </pre>
                </>
              )}
              {transaction.paycenterResponse && (
                <>
                  <h4>Response</h4>
                  <pre style={{ background: '#f5f5f5', padding: '10px', borderRadius: '4px', overflow: 'auto' }}>
                    {JSON.stringify(transaction.paycenterResponse, null, 2)}
                  </pre>
                </>
              )}
            </Tabs.TabPane>
          )}
        </Tabs>
      )}
    </Modal>
  );
}
